import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { RoutesRouter } from "../../models/Routes";
import { useTranslation } from "react-i18next";
import EmptyTable from "../../components/EmptyTable/EmptyTable";
import Button from "../../components/Button/Button";
import SnackChip from "../../components/SnackChip/SnackChip";
import Table, {
  ITableButtonOption,
  ITableHome,
} from "../../components/Table/Table";
import { CompanyResponse, SubscriptionPeriod } from "../../models/CompaniesModel";
import { Category } from "../../models/SurveyModels";
import EditPencil from "../../assets/img/edit-pencil.svg";
import Person from "../../assets/img/person.svg";
import React from "react";
import { useEndpoints } from "../../hooks/useEndpoints";
import { KeyStorage, getStorageItem } from "../../utils/localStorage";
import { urlSmartbeat } from "../../environment/environment";
import Pagination from "../../components/Pagination/Pagination";
import { IOption } from "../../components/Forms/FormInput/FormSelect/FormSelect";
import Skeleton from "react-loading-skeleton";
import Modal from "../../components/Modal/Modal";
import './Companies.scss';

export const optionsPage: IOption[] = [
  { "1": "1" },
  { "10": "10" },
  { "20": "20" },
  { "30": "30" },
];

const Companies: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const { getAllCompanies, exportAllSurveys, exportCompaniesAnagrafica, getCategories } =
    useEndpoints();
  const [filter, setFilter] = useState<boolean>(true);
  const [companies, setCompanies] = useState<CompanyResponse[]>();
  const [inputValue, setInputValue] = React.useState("");

  const [myLimit, setMyLimit] = useState<number>(
    Number(Object.values(optionsPage[1])[0])
  );

  const [skip, setSkip] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState<'surveys' | 'companies'>('surveys');
  const [exportCompanies, setExportCompanies] = useState<CompanyResponse[]>([]);
  const [exportFilters, setExportFilters] = useState<{
    company_ids: string[];
    survey_name: string;
    date_from: string;
    date_to: string;
    question_text: string;
    category_ids: string[];
  }>({
    company_ids: [],
    survey_name: '',
    date_from: '',
    date_to: '',
    question_text: '',
    category_ids: [],
  });
  const [isLoadingExportCompanies, setIsLoadingExportCompanies] = useState(false);
  const [exportCategories, setExportCategories] = useState<Category[]>([]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const openExportModal = async (mode: 'surveys' | 'companies' = 'surveys') => {
    setExportMode(mode);
    setShowExportModal(true);
    if (!exportCategories.length) {
      try {
        const cats = await getCategories();
        const sorted = [...(cats ?? [])].sort((a, b) =>
          (a.name ?? '').localeCompare(b.name ?? ''),
        );
        setExportCategories(sorted);
      } catch (err) {
        console.error('Failed to load categories for export filter', err);
      }
    }
    if (exportCompanies.length) return;
    setIsLoadingExportCompanies(true);
    try {
      const [enabledResp, disabledResp] = await Promise.all([
        getAllCompanies({ limit: 1000, skip: 0, enabled: true }),
        getAllCompanies({ limit: 1000, skip: 0, enabled: 'false' }),
      ]);
      const merged: CompanyResponse[] = [
        ...(enabledResp?.items ?? []),
        ...(disabledResp?.items ?? []),
      ];
      merged.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      setExportCompanies(merged);
    } finally {
      setIsLoadingExportCompanies(false);
    }
  };

  const closeExportModal = () => {
    if (isExporting) return;
    setShowExportModal(false);
  };

  const toggleExportCompany = (id: string) => {
    setExportFilters((prev) => {
      const next = prev.company_ids.includes(id)
        ? prev.company_ids.filter((x) => x !== id)
        : [...prev.company_ids, id];
      return { ...prev, company_ids: next };
    });
  };

  const handleExportSurveys = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const filters: {
        company_ids?: string;
        survey_name?: string;
        date_from?: string;
        date_to?: string;
        question_text?: string;
        category_ids?: string;
      } = {};
      if (exportFilters.company_ids.length)
        filters.company_ids = exportFilters.company_ids.join(',');
      if (exportFilters.survey_name.trim())
        filters.survey_name = exportFilters.survey_name.trim();
      if (exportFilters.date_from)
        filters.date_from = new Date(exportFilters.date_from).toISOString();
      if (exportFilters.date_to) {
        const to = new Date(exportFilters.date_to);
        to.setHours(23, 59, 59, 999);
        filters.date_to = to.toISOString();
      }
      if (exportFilters.question_text.trim())
        filters.question_text = exportFilters.question_text.trim();
      if (exportFilters.category_ids.length)
        filters.category_ids = exportFilters.category_ids.join(',');
      const response = await exportAllSurveys(filters);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'survey-export.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        setShowExportModal(false);
      }
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCompaniesAnagrafica = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const filters: { company_ids?: string } = {};
      if (exportFilters.company_ids.length)
        filters.company_ids = exportFilters.company_ids.join(',');
      const response = await exportCompaniesAnagrafica(filters);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'anagrafiche-aziende.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        setShowExportModal(false);
      }
    } catch (err) {
      console.error('Export companies failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const generateButtons = useCallback(
    (
      callback: (
        value: NonNullable<any>,
        role: KeyStorage["me"]["role"]
      ) => ITableButtonOption
    ): Array<ITableButtonOption> => {
      if (!companies) return [] as Array<ITableButtonOption>;
      return companies.map((company) => {
        return callback(company, getStorageItem("me")!.role);
      });
    },
    [companies]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  useEffect(() => {
    setIsLoading(true);
    getAllCompanies({
      limit: myLimit,
      skip: skip,
      enabled: filter || 'false',
      name: debouncedInputValue,
    }).then((resp) => {
      if (resp?.items) {
        setCompanies(resp.items);
      }
      if (resp?.count) {
        setCount(resp.count);
      }
      setIsLoading(false);
    });
  }, [getAllCompanies, filter, debouncedInputValue, myLimit, count, skip]);
  const buttonsOption = useMemo(
    () =>
      generateButtons((resource: any) => {
        let buttons: ITableButtonOption = [];
        buttons.push({
          onClick: (index: number) => {
            navigate(
              `${RoutesRouter.companies}/${
                companies && companies[index].id
              }/edit`
            );
          },
          label: `${t("home.edit")}`,
          colorTheme: "blue",
          icon: <img src={EditPencil} alt="edit" />,
        });

        if (resource.staffToken) {
          buttons.push({
            onClick: (index) => {
              window.open(
                `${urlSmartbeat}?token=${
                  companies && companies[index].staffToken
                }`,
                "_blank"
              );
            },
            label: `${t("login.buttonSubmit")}`,
            colorTheme: "blue",
            icon: <img src={Person} alt="go" />,
          });
        }
        return buttons;
      }),
    [companies, generateButtons, navigate, t]
  );

  const filtersLine = () => (
    <div className="resources-searching companies-searching my-3">
      <div className="form-group">
        <input
          id="searchResource"
          type="text"
          value={inputValue}
          placeholder={t("home.companies.emptyTable.inputPlaceholder") as string}
          onChange={handleInputChange}
          className="form-control filter-questions"
        />
        <i className="icon-search"></i>
        {inputValue && (
          <i
            className="icon-close"
            onClick={() => {
              setInputValue("");
            }}
          ></i>
        )}
      </div>
      <Button
        onClick={() => openExportModal('surveys')}
        disabled={isExporting}
      >
        {isExporting ? t("home.companies.exporting") : t("home.companies.exportSurveys")}
      </Button>
      <div style={{ width: 12 }} />
      <Button
        onClick={() => openExportModal('companies')}
        disabled={isExporting}
      >
        {isExporting ? t("home.companies.exporting") : t("home.companies.exportCompanies")}
      </Button>
      <div style={{ width: 12 }} />
      <Button onClick={() => navigate(RoutesRouter.addCompany)}>
        {t("home.companies.emptyTable.labelButton")}
      </Button>
    </div>
  );

  const handleClickTr = (index: number) => {
    navigate(`${RoutesRouter.companies}/${companies![index].id}/edit`);
  };

  /**
   * Stato canone:
   * - 'red' = nessun canone OPPURE periodo più recente già scaduto
   * - 'yellow' = periodo attivo che scade entro 60 giorni
   * - 'none' = canone attivo non in scadenza
   */
  const subscriptionStatus = (
    subs?: SubscriptionPeriod[],
  ): 'red' | 'yellow' | 'none' => {
    if (!subs || subs.length === 0) return 'red';
    const sorted = [...subs].sort(
      (a, b) =>
        new Date(b.endDate).getTime() - new Date(a.endDate).getTime(),
    );
    const latest = sorted[0];
    const endTs = new Date(latest.endDate).getTime();
    const now = Date.now();
    const days60 = 60 * 24 * 60 * 60 * 1000;
    if (endTs <= now) return 'red';
    if (endTs - now <= days60) return 'yellow';
    return 'none';
  };

  const titles: ITableHome["titles"] = [
    {
      textAlign: "start",
      label: t("home.companies.headTable.name"),
    },
    {
      textAlign: "start",
      label: t("home.companies.headTable.admin"),
    },
    {
      textAlign: "start",
      label: t("home.companies.headTable.hrEmail"),
    },
    {
      textAlign: "start",
      label: t("home.companies.headTable.hrPhone"),
    },
  ];

  return (
    <div className=" container-custom">
      <div className="container__top">
        <div>
          <h1 className="title-page"> {t("home.companies.companies")}</h1>
          <p>{t("home.companies.subtitle")}</p>
        </div>
        {filtersLine()}
      </div>
      <div className="resources-searching filters">
        <SnackChip
          filters
          handleCLick={() => {
            setFilter(true);
          }}
          isActive={filter}
          theme="filters"
          label={t("home.companies.filters.active")}
        />
        <SnackChip
          filters
          isActive={!filter}
          handleCLick={() => {
            setFilter(false);
          }}
          theme="filters"
          label={t("home.companies.filters.disabled")}
        />
      </div>
      <div className="container-table">
        {!isLoading && companies && companies.length > 0 && (
          <Table
            title="companies"
            data={companies?.map((company) => {
              const status = subscriptionStatus(company.subscriptions);
              const dotTooltip =
                status === 'red'
                  ? t('home.companies.subscriptionStatus.red')
                  : status === 'yellow'
                  ? t('home.companies.subscriptionStatus.yellow')
                  : '';
              return ({
              name: (
                <div className="d-flex align-items-center">
                  <div className="avatar">{`${
                    company.name.slice(0, 2) || ""
                  }`}</div>
                  <span>{company.name}</span>
                  {status !== 'none' && (
                    <span
                      className={`subscription-dot subscription-dot--${status}`}
                      title={dotTooltip as string}
                    />
                  )}
                </div>
              ),
              hrName: `${company.hrName || ""} ${company.hrSurname || ""}`,
              hrEmail: company.hrEmail,
              hrPhone: company.hrPhone,
            });
            })}
            dataKeyShow={["name", "hrName", "hrEmail", "hrPhone"]}
            titles={titles}
            addLastEmptyTh={true}
            onClickTr={handleClickTr}
            buttonsOption={buttonsOption}
          />
        )}
        {!isLoading && companies?.length === 0 && (
          <EmptyTable
            title={`${t("home.companies.emptyTable.title")} ${
              filter
                ? t("home.companies.emptyTable.enabled")
                : t("home.companies.emptyTable.disabled")
            }`}
            description={t("home.companies.emptyTable.description")}
          />
        )}
      </div>
      {!isLoading && companies && companies.length > 0 && (
        <Pagination
          limit={myLimit}
          count={count}
          skip={skip}
          setLimit={setMyLimit}
          setSkip={setSkip}
          options={optionsPage}
        />
      )}
      {isLoading && (
        <div style={{ marginTop: "2rem" }}>
          <Skeleton count={11}  height={52} />
        </div>
      )}
      {showExportModal && (
        <Modal open={showExportModal} close={closeExportModal}>
          <div className="export-filters-modal">
            <h3>
              {exportMode === 'companies'
                ? t("home.companies.exportFilters.titleCompanies")
                : t("home.companies.exportFilters.title")}
            </h3>
            <p className="description">
              {exportMode === 'companies'
                ? t("home.companies.exportFilters.descriptionCompanies")
                : t("home.companies.exportFilters.description")}
            </p>
            {exportMode === 'surveys' && (
            <>
            <div className="row-fields">
              <div className="field">
                <label htmlFor="export-date-from">
                  {t("home.companies.exportFilters.dateFrom")}
                </label>
                <input
                  id="export-date-from"
                  type="date"
                  value={exportFilters.date_from}
                  onChange={(e) =>
                    setExportFilters((prev) => ({
                      ...prev,
                      date_from: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="field">
                <label htmlFor="export-date-to">
                  {t("home.companies.exportFilters.dateTo")}
                </label>
                <input
                  id="export-date-to"
                  type="date"
                  value={exportFilters.date_to}
                  onChange={(e) =>
                    setExportFilters((prev) => ({
                      ...prev,
                      date_to: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="field">
                <label htmlFor="export-survey-name">
                  {t("home.companies.exportFilters.surveyName")}
                </label>
                <input
                  id="export-survey-name"
                  type="text"
                  placeholder={t(
                    "home.companies.exportFilters.surveyNamePlaceholder"
                  ) as string}
                  value={exportFilters.survey_name}
                  onChange={(e) =>
                    setExportFilters((prev) => ({
                      ...prev,
                      survey_name: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="row-fields">
              <div className="field">
                <label htmlFor="export-question-text">
                  {t("home.companies.exportFilters.questionText")}
                </label>
                <input
                  id="export-question-text"
                  type="text"
                  placeholder={t(
                    "home.companies.exportFilters.questionTextPlaceholder"
                  ) as string}
                  value={exportFilters.question_text}
                  onChange={(e) =>
                    setExportFilters((prev) => ({
                      ...prev,
                      question_text: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="field">
                <label htmlFor="export-category">
                  {t("home.companies.exportFilters.category")}
                </label>
                <select
                  id="export-category"
                  value={exportFilters.category_ids[0] ?? ''}
                  onChange={(e) =>
                    setExportFilters((prev) => ({
                      ...prev,
                      category_ids: e.target.value ? [e.target.value] : [],
                    }))
                  }
                >
                  <option value="">
                    {t("home.companies.exportFilters.categoryAll")}
                  </option>
                  {exportCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            </>
            )}
            <div className="field">
              <label>{t("home.companies.exportFilters.companies")}</label>
              <div className="companies-picker">
                {isLoadingExportCompanies && (
                  <Skeleton count={4} height={22} />
                )}
                {!isLoadingExportCompanies && exportCompanies.length === 0 && (
                  <div className="empty">
                    {t("home.companies.exportFilters.noCompanies")}
                  </div>
                )}
                {!isLoadingExportCompanies &&
                  exportCompanies.map((c) => (
                    <label key={c.id} className="company-option">
                      <input
                        type="checkbox"
                        checked={exportFilters.company_ids.includes(c.id!)}
                        onChange={() => toggleExportCompany(c.id!)}
                      />
                      <span>
                        {c.name}
                        {c.enabled === false
                          ? ` (${t("home.companies.filters.disabled")})`
                          : ''}
                      </span>
                    </label>
                  ))}
              </div>
            </div>
            <div className="actions">
              <Button onClick={closeExportModal} disabled={isExporting}>
                {t("home.companies.modal.cancel")}
              </Button>
              <Button
                onClick={
                  exportMode === 'companies'
                    ? handleExportCompaniesAnagrafica
                    : handleExportSurveys
                }
                disabled={isExporting}
              >
                {isExporting
                  ? t("home.companies.exporting")
                  : t("home.companies.exportFilters.confirm")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Companies;
